// =====================
// Auth API Utility
// =====================
// Handles all authentication-related API calls and global session expiry logic.

import React from 'react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { SignupData, LoginData, GoogleAuthData, AuthResponse, GoogleAuthResponse } from '../../entities/auth/types';
import { useToast } from '../../hooks/useToast';
import { useAuth } from './context';
import { useNavigate } from 'react-router-dom';
import { getGuestToken, clearGuestSession, openGuestWall } from './guest';
import { loginPathWithNext } from './nextPath';

// --- API BASE URL ---
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

// --- AXIOS INSTANCE SETUP ---
/**
 * Axios instance for authenticated API requests.
 * Automatically attaches JWT token from localStorage if available.
 */
export const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Anonymous access: a guest session rides on X-Guest-Token instead of a
    // JWT. Only guest-enabled endpoints accept it (spec §3); everything else
    // 401s, which the interceptor below turns into the signup wall.
    const guestToken = getGuestToken();
    if (guestToken) {
      (config.headers as any)['X-Guest-Token'] = guestToken;
    }
  }
  return config;
});

// --- GUEST WALL (GLOBAL INTERCEPTOR) ---
// For a guest (no JWT, guest token present), a 401/403 means "this action
// needs an account" — every mutation endpoint is JWT-only by design. Open the
// signup wall dialog instead of surfacing a raw error. A GUEST_INVALID code
// means the token itself is stale (claimed or purged): drop it locally.
authApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const hasAuthToken = !!localStorage.getItem('authToken');
    if (!hasAuthToken && getGuestToken() && (status === 401 || status === 403)) {
      const data = error.response?.data as any;
      if (data?.code === 'GUEST_INVALID') {
        clearGuestSession();
      } else {
        openGuestWall(typeof data?.error === 'string' && data?.code ? data.error : undefined);
      }
    }
    return Promise.reject(error);
  }
);

// --- SESSION EXPIRY/REFRESH HANDLING (GLOBAL INTERCEPTOR) ---
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await authApi.post('/auth/refresh-token');
    const newToken = res.data?.token;
    if (newToken) {
      localStorage.setItem('authToken', newToken);
      return newToken;
    }
    return null;
  } catch {
    return null;
  }
}

authApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const msg = (error.response?.data as any)?.error || (error.response?.data as any)?.message || '';
    const tokenExpired = status === 401 || status === 403 || /expired/i.test(msg);
    const requestUrl = (originalRequest?.url || '') as string;
    const isAuthEndpoint = /\/auth\/(login|signup|google|logout|refresh-token)/.test(requestUrl);
    const hasToken = !!localStorage.getItem('authToken');

    // Only attempt silent refresh for non-auth endpoints when a token exists
    if (tokenExpired && !originalRequest._retry && hasToken && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (token) {
              originalRequest.headers = originalRequest.headers || {};
              (originalRequest.headers as any).Authorization = `Bearer ${token}`;
              resolve(authApi(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      pendingQueue.forEach((cb) => cb(newToken));
      pendingQueue = [];
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
        return authApi(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

// --- SESSION EXPIRY HANDLING (TOAST + REDIRECT) ---
let isHandlingSessionExpiry = false;

export function useSessionExpiryInterceptor() {
  const { showToast } = useToast();
  const { logout } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    const interceptor = authApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const message = error.response?.data?.error || error.response?.data?.message || '';
        const requestUrl: string = error.config?.url || '';
        const isAuthEndpoint = /\/auth\/(login|signup|google|logout|refresh-token)/.test(requestUrl);
        const hasToken = !!localStorage.getItem('authToken');
        const tokenHardFail = (status === 401 || status === 403) && /invalid|expired/i.test(message);
        // Only handle session expiry when we actually have a session and it's not an auth endpoint
        if (tokenHardFail && !isHandlingSessionExpiry && hasToken && !isAuthEndpoint) {
          isHandlingSessionExpiry = true;
          await logout();
          showToast({ title: 'Session Expired', message: 'Please log in again.', type: 'info', duration: 6000 });
          // Carry the page the user was on into ?next= so logging back in
          // returns them there (same contract as RequireAuth's redirect).
          navigate(loginPathWithNext(window.location));
          setTimeout(() => { isHandlingSessionExpiry = false; }, 2000);
        }
        return Promise.reject(error);
      }
    );
    return () => authApi.interceptors.response.eject(interceptor);
  }, [logout, showToast, navigate]);
}

// --- AUTH API FUNCTIONS ---
export const signup = async (data: SignupData): Promise<AuthResponse> => {
  const response = await authApi.post('/auth/signup', data);
  return response.data;
};

export const login = async (data: LoginData): Promise<AuthResponse> => {
  const response = await authApi.post('/auth/login', data);
  return response.data;
};

export const googleLogin = async (data: GoogleAuthData): Promise<GoogleAuthResponse> => {
  const response = await authApi.post('/auth/google', data);
  return response.data;
};

export const logout = async (): Promise<void> => {
  try {
    await authApi.post('/auth/logout');
  } catch (error) {
    console.warn('Logout failed on server, clearing local storage');
  }
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
};

export const getCurrentUser = async (): Promise<AuthResponse['user']> => {
  const response = await authApi.get('/auth/me');
  return response.data;
};

// Save the signed-in user's interests (My Trips welcome card). `otherText`
// is the free text behind the 'other' chip; the backend drops it when
// 'other' is not picked. Returns what was actually persisted.
export const updateMyInterests = async (
  interests: string[],
  otherText?: string
): Promise<{ interests: string[]; interestsOther: string | null }> => {
  const response = await authApi.patch('/users/me/interests', { interests, otherText });
  return response.data;
};
